import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { API } from '../services';
import { useAuth } from './AuthContext';
import { secureStorage } from '../utilities/securityUtils';
import { USER_ROLE_KEYS, PAGE_KEYS } from '../utilities/constants';

export const UserRolesAndPermissionsContext = createContext(null);

export const UserRolesAndPermissionsProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const [userRole, setUserRole] = useState(
    () => secureStorage.getItem('userRole') || null
  );
  const [userPermissions, setUserPermissions] = useState(() => {
    const stored = secureStorage.getItem('userPermissions');
    try {
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [selectedOrg, setSelectedOrg] = useState(
    () => secureStorage.getItem('selectedOrg') || null
  );
  const [organisationFeatures, setOrganisationFeatures] = useState([]);
  const [userDetails, setUserDetails] = useState([]);

  const fetchUserRolesAndPermissions = async () => {
    try {
      const { data } = await API.getPermissionsList();
      setUserRole(data?.role);

      const allowedEmails = (process.env.REACT_APP_TRIP_SCHEDULING_EMAILS || '')
        .split(',')
        .map(e => e.trim().toLowerCase());
      const userEmail = (sessionStorage.getItem('email') || '').toLowerCase();

      const apiAccess =
        data?.dashboard?.[PAGE_KEYS.TRIP_SCHEDULING_AND_MANAGEMENT];
      const hasFullAccess =
        allowedEmails.includes(userEmail) && apiAccess === 'full_access';

      const permissionsData = {
        ...data?.dashboard,
        [PAGE_KEYS.TRIP_SCHEDULING_AND_MANAGEMENT]: hasFullAccess
          ? 'full_access'
          : 'no_access',
      };
      setUserPermissions(permissionsData);
      secureStorage.setItem('userRole', data?.role);
      secureStorage.setItem('userPermissions', JSON.stringify(permissionsData));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to user role and permissions', err);
    }
  };

  const fetchOrganisationFeatures = async () => {
    try {
      const { data } = await API.getOrganisationFeatures();
      setOrganisationFeatures(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch organisation features', err);
    }
  };

  const getUserDetails = async () => {
    try {
      const { data } = await API.getUserDetails();
      setUserDetails(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch user details', err);
    }
  };

  useEffect(() => {
    if (!userRole || Object.keys(userPermissions).length === 0) {
      isLoggedIn && fetchUserRolesAndPermissions();
    }
    isLoggedIn && fetchOrganisationFeatures();
    isLoggedIn && getUserDetails();
  }, []);

  const isViewOnly = page => {
    const access = userPermissions?.[page];
    return access === 'view_only' || access === 'no_access';
  };

  const isPageAccessible = page => {
    const access = userPermissions?.[page];
    return access === 'full_access' || access === 'view_only';
  };

  const isFeatureAccessible = featureName => {
    if (userRole === USER_ROLE_KEYS.SUPER_ADMIN) {
      const selectedOrgFeatures = Array.isArray(organisationFeatures)
        ? organisationFeatures.find(org => org?.org_id === selectedOrg)
        : organisationFeatures;

      const feature = selectedOrgFeatures?.application_feature_list?.find(
        feat => feat?.feature_name === featureName
      );

      return feature ? feature.is_active : false;
    }
    const feature = organisationFeatures?.application_feature_list?.find(
      feat => feat?.feature_name === featureName
    );
    return feature ? feature.is_active : false;
  };

  const contextValue = useMemo(
    () => ({
      fetchUserRolesAndPermissions,
      fetchOrganisationFeatures,
      organisationFeatures,
      userRole,
      userPermissions,
      isViewOnly,
      isPageAccessible,
      isFeatureAccessible,
      getUserDetails,
      userDetails,
      selectedOrg,
      setSelectedOrg,
    }),
    [userRole, userPermissions, organisationFeatures, userDetails, selectedOrg]
  );

  return (
    <UserRolesAndPermissionsContext.Provider value={contextValue}>
      {children}
    </UserRolesAndPermissionsContext.Provider>
  );
};

UserRolesAndPermissionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useUserRolesAndPermissions = () =>
  useContext(UserRolesAndPermissionsContext);
